package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.core.uuidV7
import com.dotdrives.model.ActivityEvent
import com.dotdrives.model.Giver
import com.dotdrives.model.Note
import com.dotdrives.repository.GiverRepository
import com.dotdrives.repository.TimelineFilter
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlin.time.Clock

data class GiverDetailUiState(
    val loading: Boolean = false,
    val giver: Giver? = null,
    val timeline: List<ActivityEvent> = emptyList(),
    val notes: List<Note> = emptyList(),
    val timelineFilter: TimelineFilter = TimelineFilter.All,
    val noteDraftTitle: String = "",
    val noteDraftBody: String = "",
    val savingNote: Boolean = false,
    val error: String? = null,
) {
    val showingAllTimeline: Boolean
        get() = timelineFilter == TimelineFilter.All

    val showingNotesTimeline: Boolean
        get() = timelineFilter == TimelineFilter.Notes
}

class GiverDetailController(
    private val repo: GiverRepository,
    scope: CoroutineScope,
    private val orgId: String,
) : DotController<GiverDetailUiState>(GiverDetailUiState(), scope) {
    private var loadGeneration = 0L
    private var openedGiverId: String? = null
    private var pendingOptimisticEventId: String? = null
    private var noteSaveInFlight = false

    fun open(giverId: String) {
        openedGiverId = giverId
        pendingOptimisticEventId = null
        val generation = nextGeneration()
        update {
            GiverDetailUiState(
                loading = true,
                timelineFilter = TimelineFilter.All,
            )
        }
        scope.launch {
            try {
                val loaded = coroutineScope {
                    val giver = async { repo.get(orgId, giverId) }
                    val timeline = async {
                        repo.timeline(
                            orgId = orgId,
                            giverId = giverId,
                            filter = TimelineFilter.All,
                            limit = TIMELINE_LIMIT,
                        )
                    }
                    val notes = async { repo.notes(orgId, giverId) }
                    DetailLoad(
                        giver = giver.await().getOrThrow(),
                        timeline = timeline.await().getOrThrow(),
                        notes = notes.await().getOrThrow(),
                    )
                }
                if (!isCurrent(generation)) return@launch
                update { current ->
                    if (isCurrent(generation)) {
                        current.copy(
                            loading = false,
                            giver = loaded.giver,
                            timeline = loaded.timeline,
                            notes = loaded.notes,
                            error = null,
                        )
                    } else {
                        current
                    }
                }
            } catch (error: Throwable) {
                publishError(error, generation)
            }
        }
    }

    fun setTimelineFilter(f: TimelineFilter) {
        val giverId = openedGiverId ?: return
        val supersededOptimisticEventId = pendingOptimisticEventId
        val generation = nextGeneration()
        update {
            it.copy(
                timeline = it.timeline.filterNot { event ->
                    event.id == supersededOptimisticEventId
                },
                timelineFilter = f,
                loading = true,
                error = null,
            )
        }
        scope.launch {
            try {
                repo.timeline(orgId, giverId, f, TIMELINE_LIMIT)
                    .onSuccess { timeline ->
                        if (!isCurrent(generation)) return@onSuccess
                        update { current ->
                            if (isCurrent(generation)) {
                                current.copy(
                                    loading = false,
                                    timeline = timeline,
                                    error = null,
                                )
                            } else {
                                current
                            }
                        }
                    }
                    .onFailure { publishError(it, generation) }
            } catch (error: Throwable) {
                publishError(error, generation)
            }
        }
    }

    fun showAllTimeline() {
        setTimelineFilter(TimelineFilter.All)
    }

    fun showNotesTimeline() {
        setTimelineFilter(TimelineFilter.Notes)
    }

    fun updateNoteDraft(title: String, body: String) {
        update {
            it.copy(
                noteDraftTitle = title,
                noteDraftBody = body,
                error = null,
            )
        }
    }

    fun saveNote() {
        val current = state.value
        val giverId = openedGiverId ?: return
        if (noteSaveInFlight) return
        if (current.noteDraftBody.isBlank()) {
            update { it.copy(error = "Note body is required") }
            return
        }
        val title = current.noteDraftTitle.trim()
        val body = current.noteDraftBody.trim()
        val generation = loadGeneration
        noteSaveInFlight = true
        val optimisticEvent = ActivityEvent(
            id = uuidV7(),
            verb = NOTE_ADDED_VERB,
            subjectType = GIVER_SUBJECT_TYPE,
            subjectId = giverId,
            payload = buildJsonObject {
                put("title", title)
                put("snippet", body.take(NOTE_SNIPPET_LENGTH))
            },
            occurredAt = Clock.System.now(),
            actorUserId = null,
        )
        pendingOptimisticEventId = optimisticEvent.id
        update {
            it.copy(
                timeline = listOf(optimisticEvent) + it.timeline,
                savingNote = true,
                error = null,
            )
        }
        scope.launch {
            try {
                repo.addNote(orgId, giverId, title, body)
                    .onSuccess { note ->
                        reconcileSavedNote(giverId, note)
                    }
                    .onFailure { error ->
                        noteSaveInFlight = false
                        pendingOptimisticEventId = null
                        if (openedGiverId != giverId) {
                            return@onFailure
                        }
                        if (!isCurrent(generation)) {
                            update { it.copy(savingNote = false) }
                            return@onFailure
                        }
                        update { state ->
                            state.copy(
                                timeline = state.timeline.filterNot { event ->
                                    event.id == optimisticEvent.id
                                },
                                savingNote = false,
                                error = error.message ?: "Something went wrong",
                            )
                        }
                    }
            } catch (error: Throwable) {
                if (error is CancellationException) throw error
                noteSaveInFlight = false
                pendingOptimisticEventId = null
                if (openedGiverId != giverId) return@launch
                if (!isCurrent(generation)) {
                    update { it.copy(savingNote = false) }
                    return@launch
                }
                update {
                    it.copy(
                        timeline = it.timeline.filterNot { event ->
                            event.id == optimisticEvent.id
                        },
                        savingNote = false,
                        error = error.message ?: "Something went wrong",
                    )
                }
            }
        }
    }

    private suspend fun reconcileSavedNote(giverId: String, note: Note) {
        if (openedGiverId != giverId) {
            noteSaveInFlight = false
            pendingOptimisticEventId = null
            return
        }
        val filter = state.value.timelineFilter
        val refreshedTimeline = repo.timeline(
            orgId = orgId,
            giverId = giverId,
            filter = filter,
            limit = TIMELINE_LIMIT,
        ).getOrNull()
        noteSaveInFlight = false
        pendingOptimisticEventId = null
        if (openedGiverId != giverId) return
        update { current ->
            current.copy(
                timeline = if (current.timelineFilter == filter && refreshedTimeline != null) {
                    refreshedTimeline
                } else {
                    current.timeline
                },
                notes = listOf(note) + current.notes.filterNot { it.id == note.id },
                noteDraftTitle = "",
                noteDraftBody = "",
                savingNote = false,
                error = null,
            )
        }
    }

    private fun publishError(error: Throwable, generation: Long) {
        if (error is CancellationException) throw error
        if (!isCurrent(generation)) return
        update { current ->
            if (isCurrent(generation)) {
                current.copy(
                    loading = false,
                    error = error.message ?: "Something went wrong",
                )
            } else {
                current
            }
        }
    }

    private fun nextGeneration(): Long {
        loadGeneration += 1
        return loadGeneration
    }

    private fun isCurrent(generation: Long): Boolean =
        generation == loadGeneration
}

private data class DetailLoad(
    val giver: Giver,
    val timeline: List<ActivityEvent>,
    val notes: List<Note>,
)

private const val TIMELINE_LIMIT = 50
private const val NOTE_ADDED_VERB = "note.added"
private const val GIVER_SUBJECT_TYPE = "giver"
private const val NOTE_SNIPPET_LENGTH = 140
